import { select } from 'redux-saga/effects';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { makeSelectClientLanding } from 'containers/ClientLanding/selectors';
import _get from 'lodash/get';
import _forEach from 'lodash/forEach';
import _isEmpty from 'lodash/isEmpty';
import { getCookie } from 'utils/cookieStore';

export function* getAlgoliaRatingQuery() {
  const clientDetails = yield select(makeSelectClientDetails());
  if (!clientDetails) return '';
  const {
    averageRatingCutoff,
    expertRatingCutoff,
    userRatingCutoff,
  } = clientDetails;
  let algoliaRatingCutOffQuery = '';

  if (expertRatingCutoff) {
    algoliaRatingCutOffQuery += ` AND cutoffExpertRating >= ${expertRatingCutoff}`;
  }
  if (averageRatingCutoff) {
    const resourcesWeightings = _get(
      clientDetails,
      'metadata.algoliaResourcesWeight',
    );
    let field = 'cutoffAvgRating';
    if (resourcesWeightings === '75-25') field = 'cutoffAvgRating7525';
    else if (resourcesWeightings === '90-10') field = 'cutoffAvgRating9010';

    algoliaRatingCutOffQuery += ` AND ${field} >= ${averageRatingCutoff}`;
  }
  if (userRatingCutoff) {
    algoliaRatingCutOffQuery += ` AND cutoffUserRating >= ${userRatingCutoff}`;
  }
  return algoliaRatingCutOffQuery;
}

export function* getContentfulRatingQuery() {
  const clientDetails = yield select(makeSelectClientDetails());
  let contentfulRatingQuery = {};
  if (!_isEmpty(clientDetails)) {
    const {
      averageRatingCutoff,
      expertRatingCutoff,
      userRatingCutoff,
    } = clientDetails;
    if (expertRatingCutoff) {
      contentfulRatingQuery = {
        ...contentfulRatingQuery,
        'fields.calculatedExpertRating[gte]': expertRatingCutoff,
      };
    }
    if (averageRatingCutoff) {
      contentfulRatingQuery = {
        ...contentfulRatingQuery,
        'fields.calculatedAverageRating[gte]': averageRatingCutoff,
      };
    }
    if (userRatingCutoff) {
      contentfulRatingQuery = {
        ...contentfulRatingQuery,
        'fields.calculatedUserRating[gte]': userRatingCutoff,
      };
    }
  }
  return contentfulRatingQuery;
}

export function* getTopicsIdQuery() {
  const clientDetails = yield select(makeSelectClientDetails());
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  const excludedTopicsId = !_isEmpty(clientExcludedTopic)
    ? clientExcludedTopic.map(topic => topic.sys.id)
    : [];
  return excludedTopicsId;
}

export function* getTopicsTitleQuery() {
  const clientDetails = yield select(makeSelectClientDetails());
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  const excludedTopicsTitle = !_isEmpty(clientExcludedTopic)
    ? clientExcludedTopic.map(topic => topic.title)
    : [];
  return excludedTopicsTitle;
}

export function* getAlgoliaTopicsQuery(field, excludeOnly = true) {
  const clientDetails = yield select(makeSelectClientDetails());
  const clientLanding = yield select(makeSelectClientLanding());
  let algoliaTopicsQuery = '';
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  const excludedTopicTitle = !_isEmpty(clientExcludedTopic)
    ? clientExcludedTopic.map(topic => topic.title)
    : [];
  if (!_isEmpty(excludedTopicTitle)) {
    _forEach(excludedTopicTitle, id => {
      algoliaTopicsQuery += ` AND NOT ${field}:'${id}'`;
    });
  }

  if (!excludeOnly && !_isEmpty(clientDetails)) {
    const topicsCollections = _get(clientLanding, 'topTopicsCollection.items');
    if (!_isEmpty(topicsCollections)) {
      algoliaTopicsQuery += 'AND ';
      _forEach(topicsCollections, ({ title }, index) => {
        algoliaTopicsQuery += `allTopics.title:'${title}'`;
        if (index !== topicsCollections.length - 1) {
          algoliaTopicsQuery += ' OR ';
        }
      });
    }
  }

  return algoliaTopicsQuery;
}

export function* getContentfulTopicsQuery(isTopic) {
  const clientDetails = yield select(makeSelectClientDetails());
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  let contentfulTopicsQuery = {};
  const excludedTopicIDs = !_isEmpty(clientExcludedTopic)
    ? clientExcludedTopic.map(topic => topic.sys.id)
    : [];
  if (!_isEmpty(excludedTopicIDs)) {
    if (isTopic)
      contentfulTopicsQuery = {
        'sys.id[nin]': excludedTopicIDs.join(','),
      };
    else
      contentfulTopicsQuery = {
        'fields.topics.sys.id[nin]': excludedTopicIDs.join(','),
      };
  }
  return contentfulTopicsQuery;
}

export function getArticlesAlgoliaQuery() {
  const brand = getCookie('brand');
  const branded = !!brand && brand !== 'none';
  let query = 'hasClient:false';
  if (branded) {
    query += ` OR client.shortName:${brand.toLowerCase()} `;
  }
  return query;
}

export function getContenfulFilters(
  clientExcludedTopic,
  averageRatingCutoff,
  expertRatingCutoff,
  userRatingCutoff,
  isTopic,
) {
  // topics query
  let contentfulTopicsQuery = {};
  const excludedTopicIDs = !_isEmpty(clientExcludedTopic)
    ? clientExcludedTopic.map(topic => topic.sys.id)
    : [];
  if (!_isEmpty(excludedTopicIDs)) {
    if (isTopic)
      contentfulTopicsQuery = {
        'sys.id[nin]': excludedTopicIDs.join(','),
      };
    else
      contentfulTopicsQuery = {
        'fields.topics.sys.id[nin]': excludedTopicIDs.join(','),
      };
  }

  // rating query
  let contentfulRatingQuery = {};
  if (expertRatingCutoff) {
    contentfulRatingQuery = {
      ...contentfulRatingQuery,
      'fields.calculatedExpertRating[gte]': expertRatingCutoff,
    };
  }
  if (averageRatingCutoff) {
    contentfulRatingQuery = {
      ...contentfulRatingQuery,
      'fields.calculatedAverageRating[gte]': averageRatingCutoff,
    };
  }
  if (userRatingCutoff) {
    contentfulRatingQuery = {
      ...contentfulRatingQuery,
      'fields.calculatedUserRating[gte]': userRatingCutoff,
    };
  }

  return {
    ...contentfulTopicsQuery,
    ...contentfulRatingQuery,
  };
}
export function getAlgoliaFilters(
  clientExcludedTopic,
  averageRatingCutoff,
  expertRatingCutoff,
  userRatingCutoff,
  resourcesWeightings,
  isTopic,
) {
  // topics query
  let algoliaTopicsQuery = '';
  const excludedTopicTitle = !_isEmpty(clientExcludedTopic)
    ? clientExcludedTopic.map(topic => topic.title)
    : [];
  if (!_isEmpty(excludedTopicTitle) && isTopic) {
    _forEach(excludedTopicTitle, id => {
      algoliaTopicsQuery += ` AND NOT topics:'${id}'`;
    });
  }

  // rating query
  let algoliaRatingCutOffQuery = '';
  if (expertRatingCutoff) {
    algoliaRatingCutOffQuery += ` AND cutoffExpertRating >= ${expertRatingCutoff}`;
  }
  if (averageRatingCutoff) {
    let field = 'cutoffAvgRating';
    if (resourcesWeightings === '75-25') field = 'cutoffAvgRating7525';
    else if (resourcesWeightings === '90-10') field = 'cutoffAvgRating9010';

    algoliaRatingCutOffQuery += ` AND ${field} >= ${averageRatingCutoff}`;
  }
  if (userRatingCutoff) {
    algoliaRatingCutOffQuery += ` AND cutoffUserRating >= ${userRatingCutoff}`;
  }

  return {
    algoliaTopicsQuery,
    algoliaRatingCutOffQuery,
  };
}
